"use client"

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle, DialogTrigger } from "../ui/dialog";
import { useState } from "react";
import { deploymentAvailaility } from "@/services/deployment.service";
import { Input } from "../ui/input";
import { z } from "zod";
import { CreateStatus } from "@/constants/status";
import { FtpFormSchema } from "@/schemas/ftp.schema";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { createConnection } from "@/services/ftpConnections.service";
import { ToggleInput, ToggleLabel, ToggleSpan } from "../ui/toggle";

interface Props {
    updateData: () => void
}

export default function FtpModal(props: Props) {
    const [open, setOpen] = useState(false)
    const [createStatus, setCreateStatus] = useState(CreateStatus.standBy);
    const [failedMessage, setFailedMessage] = useState("");
    const [connectionApproved, setConnectionApproved] = useState(false);

    const form = useForm<z.infer<typeof FtpFormSchema>>({
        resolver: zodResolver(FtpFormSchema),
        defaultValues: {
          Name:"",
          Server: "",
          Username: "",
          Password: "",
        },
    });

    async function CreateConnection(data: z.infer<typeof FtpFormSchema>){
        setCreateStatus(CreateStatus.onGoing);

        const authCheck = await deploymentAvailaility();
        if(authCheck === false){
            console.log("Auth Failed");
            setCreateStatus(CreateStatus.notAuthroized);
            return;
        }

        const formData = new FormData();
        formData.append('name', data!.Name);
        formData.append('server', data!.Server);
        formData.append('username', data!.Username);
        formData.append('password', data!.Password);
        formData.append('cbApproved', connectionApproved.toString());

        const status = await createConnection(formData);
        if(status != "Connection created"){
            console.log(status);
            setFailedMessage("Connection creation failed. Check console.log for more information.<br>Otherwise, here: ".concat(status));
            setCreateStatus(CreateStatus.failed);
            return;
        }

        handleSuccess();
        return;
    }

    function errorHandler(data: unknown){
        console.log("Error Occoured:")
        console.log(data);
    }

    const handleSuccess = () => {
        setCreateStatus(CreateStatus.completed);
        setTimeout(() => {
            props.updateData();
            setOpen(false);
        }, 3000);
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button className="cursor-pointer" onClick={() => {
                }}>Create FTP Connection</Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
                <DialogTitle>Create FTP Connection</DialogTitle>
                <DialogDescription> 
                </DialogDescription>
                <div>
                    {(createStatus === CreateStatus.standBy ||
                        createStatus === CreateStatus.notAuthroized) && (
                            <Form {...form}>
                                <form 
                                    autoComplete="off"
                                    className="grid gap-y-4"
                                    onSubmit={form.handleSubmit(CreateConnection, errorHandler)}
                                >
                                    <div className="grid grid-cols-4 gap-4">
                                        <div className="col-start-1 col-end-4 rounded-md border-black">
                                            <FormField
                                                control={form.control}
                                                name="Name"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Name</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                            placeholder="Name"
                                                            {...field}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="Server"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Server</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                            placeholder="Server"
                                                            {...field}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="Username"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Username</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                            placeholder="Username"
                                                            {...field}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="Password"
                                                render={({ field }) => (
                                                    <FormItem className="mb-2">
                                                        <FormLabel>Password</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                            placeholder="Password"
                                                            {...field}
                                                            />
                                                        </FormControl>

                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <div className="col-start-4 col-end-4 rounded-md border-black">
                                            <ToggleLabel>
                                                Clearbook Approved?
                                                <ToggleInput checked={connectionApproved} onChange={() => {
                                                        setConnectionApproved(!connectionApproved);
                                                    }}/>
                                                <ToggleSpan/>
                                            </ToggleLabel>
                                        </div>
                                    </div>
                                    <div className="col-start-1 col-end-7">
                                        {createStatus == CreateStatus.notAuthroized && (
                                        <h1 className="w-full text-xl font-bold text-black dark:text-white mb-2 py-1 px-2 rounded-md bg-red-500">
                                            Authorization to deploy failed
                                        </h1>
                                        )}
                                        <Button
                                            className="w-full mt-4 cursor-pointer"
                                            type="submit"
                                        >
                                            Create Connection
                                        </Button>
                                    </div>
                                </form>
                            </Form>
                    )}
                </div>
                <DialogFooter>
                    <div className="grid grid-cols-6 gap-4">
                        <div className="col-start-1 col-end-7 rounded-md border-black">
                            {createStatus == CreateStatus.onGoing && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Creating Entry...</p>
                                </div>
                            )}
                            {createStatus == CreateStatus.completed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>Created Entry! Will refresh this page momentarily</p>
                                </div>
                            )}
                            {createStatus == CreateStatus.failed && (
                                <div className="flex flex-col w-full max-w-2xl  gap-y-4">
                                    <p>{failedMessage}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}